<div class="pt-3 pl-md-4" *ngIf="!accountService.getMyAccount().can_manage_members">
  <h2>You do not have permission to manage customers.</h2>
</div>

<div class="pt-3 pl-md-4" *ngIf="serverError !== null">
  <h2>Server Error: {{ serverError }}</h2>
</div>

<div class="pt-3 pl-md-4 mb-6"
     *ngIf="accountService.getMyAccount().can_manage_members && serverError === null">

  <h2>Merge Customer Accounts</h2>

  <p>
    Merging customer accounts will move all associated items from the secondary customer to the primary customer. This includes memberships, check-in history, documents, and all other associated collections.
  </p>

  <p>
    <b class="text-danger">After merging, the secondary customer account will no longer be accessible.</b> Before merging, check the details listed below and ensure that the primary customer has all of the info you wish to keep. If the secondary customer has a profile image but the primary customer does not, the profile image will be copied to the primary customer.
  </p>

  <div class="alert alert-success" *ngIf="mergedCustomerId">
    Customers merged successfully.
    <a routerLink="/customers/{{ mergedCustomerId }}">View the merged customer account.</a>
  </div>

  <div class="alert alert-danger" *ngIf="mergeErrorMessage">
    {{ mergeErrorMessage }}
  </div>

  <p>
    <a href="#"
       (click)="$event.preventDefault();swapCustomers();">
      Swap primary and secondary customer
    </a>
  </p>

  <div class="row">

    <div class="col">
      <form (ngSubmit)="loadPrimaryCustomer()">
        <div class="form-group">
          <label for="primaryCustomerIdInput">Primary Customer (Customer ID #)</label>
          <div class="input-group">
            <input class="form-control" id="primaryCustomerIdInput" name="primaryCustomerId"
                   type="number" min="1" step="1"
                   [(ngModel)]="primaryCustomerId"
                   (ngModelChange)="resetPrimaryCustomer()">
            <div class="input-group-append">
              <button type="submit" class="btn btn-outline-secondary"
                      [disabled]="loadingPrimaryCustomer"
                      [class.disabled]="loadingPrimaryCustomer">
                Submit
              </button>
            </div>
          </div>
        </div>
      </form>

      <div class="alert alert-info" *ngIf="loadingPrimaryCustomer">
        Loading...
      </div>

      <div class="alert alert-danger" *ngIf="primaryCustomerNotFound">
        Customer Not Found
      </div>

      <div *ngIf="!loadingPrimaryCustomer && !primaryCustomerNotFound && primaryCustomer">
        <dl>
          <dt>First Name</dt>
          <dd>{{ primaryCustomer.first_name }}</dd>
          <dt>Last Name</dt>
          <dd>{{ primaryCustomer.last_name }}</dd>
          <dt>Birthday</dt>
          <dd>{{ dateStringFromISO8601(primaryCustomer.birthday) }}</dd>
          <dt>Created At</dt>
          <dd>
            {{ timeStringFromISO8601(primaryCustomer.created_at, orgConfig.getOrganizationConfig().time_zone) }},
            {{ dateStringFromISO8601(primaryCustomer.created_at, orgConfig.getOrganizationConfig().time_zone) }}
          </dd>
          <dt>Email</dt>
          <dd *ngIf="primaryCustomer.email">{{ primaryCustomer.email }}</dd>
          <dd *ngIf="!primaryCustomer.email" class="text-muted">None</dd>
          <dt>Profile Image</dt>
          <dd>
            <img *ngIf="!primaryCustomer.profile_image"
               src="/assets/images/photo_placeholder.png"
               class="customer-photo my-2">
            <img *ngIf="primaryCustomer.profile_image"
                 [src]="primaryCustomer.profile_image"
                 class="customer-photo my-2">
          </dd>
          <dt>Street Address 1</dt>
          <dd *ngIf="primaryCustomer.address_street_1">{{ primaryCustomer.address_street_1 }}</dd>
          <dd *ngIf="!primaryCustomer.address_street_1" class="text-muted">None</dd>
          <dt>Street Address 2</dt>
          <dd *ngIf="primaryCustomer.address_street_2">{{ primaryCustomer.address_street_2 }}</dd>
          <dd *ngIf="!primaryCustomer.address_street_2" class="text-muted">None</dd>
          <dt>City</dt>
          <dd *ngIf="primaryCustomer.address_city">{{ primaryCustomer.address_city }}</dd>
          <dd *ngIf="!primaryCustomer.address_city" class="text-muted">None</dd>
          <dt>State/Province</dt>
          <dd *ngIf="primaryCustomer.address_state">{{ primaryCustomer.address_state }}</dd>
          <dd *ngIf="!primaryCustomer.address_state" class="text-muted">None</dd>
          <dt>Country</dt>
          <dd *ngIf="primaryCustomer.address_country">{{ primaryCustomer.address_country }}</dd>
          <dd *ngIf="!primaryCustomer.address_country" class="text-muted">None</dd>
          <dt>Postcode/Zip Code</dt>
          <dd *ngIf="primaryCustomer.address_postcode">{{ primaryCustomer.address_postcode }}</dd>
          <dd *ngIf="!primaryCustomer.address_postcode" class="text-muted">None</dd>
          <dt>Telephone</dt>
          <dd *ngIf="primaryCustomer.telephone">{{ primaryCustomer.telephone }}</dd>
          <dd *ngIf="!primaryCustomer.telephone" class="text-muted">None</dd>
          <dt>Barcode</dt>
          <dd *ngIf="primaryCustomer.barcode">{{ primaryCustomer.barcode }}</dd>
          <dd *ngIf="!primaryCustomer.barcode" class="text-muted">None</dd>
        </dl>
      </div>
    </div>

    <div class="col">
      <form (ngSubmit)="loadSecondaryCustomer()">
        <div class="form-group">
          <label for="secondaryCustomerIdInput">Secondary Customer (Customer ID #)</label>
          <div class="input-group">
            <input class="form-control" id="secondaryCustomerIdInput" name="secondaryCustomerId"
                   type="number" min="1" step="1"
                   [(ngModel)]="secondaryCustomerId"
                   (ngModelChange)="resetSecondaryCustomer()">
            <div class="input-group-append">
              <button type="submit" class="btn btn-outline-secondary"
                      [disabled]="loadingSecondaryCustomer"
                      [class.disabled]="loadingSecondaryCustomer">
                Submit
              </button>
            </div>
          </div>
        </div>
      </form>

      <div class="alert alert-info" *ngIf="loadingSecondaryCustomer">
        Loading...
      </div>

      <div class="alert alert-danger" *ngIf="secondaryCustomerNotFound">
        Customer Not Found
      </div>

      <div *ngIf="!loadingSecondaryCustomer && !secondaryCustomerNotFound && secondaryCustomer">
        <dl>
          <dt>First Name</dt>
          <dd>{{ secondaryCustomer.first_name }}</dd>
          <dt>Last Name</dt>
          <dd>{{ secondaryCustomer.last_name }}</dd>
          <dt>Birthday</dt>
          <dd>{{ dateStringFromISO8601(secondaryCustomer.birthday) }}</dd>
          <dt>Created At</dt>
          <dd>
            {{ timeStringFromISO8601(secondaryCustomer.created_at, orgConfig.getOrganizationConfig().time_zone) }},
            {{ dateStringFromISO8601(secondaryCustomer.created_at, orgConfig.getOrganizationConfig().time_zone) }}
          </dd>
          <dt>Email</dt>
          <dd *ngIf="secondaryCustomer.email">{{ secondaryCustomer.email }}</dd>
          <dd *ngIf="!secondaryCustomer.email" class="text-muted">None</dd>
          <dt>Profile Image</dt>
          <dd>
            <img *ngIf="!secondaryCustomer.profile_image"
               src="/assets/images/photo_placeholder.png"
               class="customer-photo my-2">
            <img *ngIf="secondaryCustomer.profile_image"
                 [src]="secondaryCustomer.profile_image"
                 class="customer-photo my-2">
          </dd>
          <dt>Street Address</dt>
          <dd *ngIf="secondaryCustomer.address_street_1">{{ secondaryCustomer.address_street_1 }}</dd>
          <dd *ngIf="!secondaryCustomer.address_street_1" class="text-muted">None</dd>
          <dt>Street Address</dt>
          <dd *ngIf="secondaryCustomer.address_street_2">{{ secondaryCustomer.address_street_2 }}</dd>
          <dd *ngIf="!secondaryCustomer.address_street_2" class="text-muted">None</dd>
          <dt>City</dt>
          <dd *ngIf="secondaryCustomer.address_city">{{ secondaryCustomer.address_city }}</dd>
          <dd *ngIf="!secondaryCustomer.address_city" class="text-muted">None</dd>
          <dt>State/Province</dt>
          <dd *ngIf="secondaryCustomer.address_city">{{ secondaryCustomer.address_city }}</dd>
          <dd *ngIf="!secondaryCustomer.address_city" class="text-muted">None</dd>
          <dt>Country</dt>
          <dd *ngIf="secondaryCustomer.address_country">{{ secondaryCustomer.address_country }}</dd>
          <dd *ngIf="!secondaryCustomer.address_country" class="text-muted">None</dd>
          <dt>Postcode/Zip Code</dt>
          <dd *ngIf="secondaryCustomer.address_postcode">{{ secondaryCustomer.address_postcode }}</dd>
          <dd *ngIf="!secondaryCustomer.address_postcode" class="text-muted">None</dd>
          <dt>Telephone</dt>
          <dd *ngIf="secondaryCustomer.telephone">{{ secondaryCustomer.telephone }}</dd>
          <dd *ngIf="!secondaryCustomer.telephone" class="text-muted">None</dd>
          <dt>Barcode</dt>
          <dd *ngIf="secondaryCustomer.barcode">{{ secondaryCustomer.barcode }}</dd>
          <dd *ngIf="!secondaryCustomer.barcode" class="text-muted">None</dd>
        </dl>
      </div>
    </div>

  </div>

  <div class="row mt-2">
    <div class="col">
      <div class="form-group mb-5"
           *ngIf="primaryCustomer && secondaryCustomer">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="confirmationCheck"
                 [(ngModel)]="confirmationChecked">
          <label class="form-check-label" for="confirmationCheck">
            I understand that this cannot be undone.
          </label>
        </div>
      </div>

      <a routerLink="/customers" class="btn btn-secondary float-left"
         [ngbTooltip]="'Submit both a primary and secondary customer ID before merging.'">
        Cancel
      </a>

      <button class="btn btn-primary float-right disabled" disabled
              *ngIf="loadingPrimaryCustomer || loadingSecondaryCustomer || loadingMerge">
        Merge
      </button>
      <ng-container *ngIf="!(loadingPrimaryCustomer || loadingSecondaryCustomer || loadingMerge)">
        <div class="float-right"
             *ngIf="!(primaryCustomer && secondaryCustomer)"
             [ngbTooltip]="'Submit both a primary and secondary customer ID before merging.'">
          <button class="btn btn-primary disabled" disabled>
            Merge
          </button>
        </div>
        <button class="btn btn-primary float-right"
                [disabled]="!confirmationChecked"
                [class.disabled]="!confirmationChecked"
                *ngIf="primaryCustomer && secondaryCustomer"
                (click)="submitMerge()">
          Merge
        </button>
      </ng-container>
    </div>
  </div>
</div>
